import React from "react";
import { ShieldCheck, Building2, Scale, FileText, Gavel, Users } from "lucide-react";

/**
 * PracticeMatrix — replaces the abstract Cyprus map illustration with a
 * contextual visual: a stylised "practice areas matrix" showing actual case
 * files with status seals, confidence pills, and bordeaux accent. Pure
 * SVG/HTML, no external assets. Designed to feel like a real legal
 * dashboard preview — the perfect lure for lawyers ("this is what I'd see").
 */
export default function PracticeMatrix({ className = "" }) {
  const cases = [
    { code: "EMP-2045", title: "Termination · Cap. 24", Icon: Users,      tone: "bg-[#FBE9EE] text-[#8B1E3F]", status: "Ready",   pct: 86 },
    { code: "COM-1187", title: "Shareholder dispute",   Icon: Building2,  tone: "bg-[#E6EEFB] text-[#2563EB]", status: "Drafting", pct: 64 },
    { code: "REA-0902", title: "Land registry · Title", Icon: Scale,      tone: "bg-[#F1EBFB] text-[#7C3AED]", status: "Review",  pct: 92 },
    { code: "FAM-0337", title: "Custody · Family Court", Icon: FileText,  tone: "bg-[#E7F4EC] text-[#15803D]", status: "Active",  pct: 71 },
    { code: "TAX-0061", title: "VAT assessment appeal", Icon: Gavel,      tone: "bg-[#FEF3C7] text-[#B45309]", status: "Ready",   pct: 78 },
    { code: "GDP-0244", title: "Data breach · N.125(I)", Icon: ShieldCheck, tone: "bg-[#E0F2F1] text-[#0F766E]", status: "Review", pct: 88 },
  ];

  return (
    <div
      className={`relative rounded-3xl overflow-hidden p-5 ${className}`}
      style={{ background: "linear-gradient(155deg, #FBFCFE 0%, #F8DBE4 50%, #E8DFF5 100%)" }}
      data-testid="practice-matrix"
    >
      {/* Ambient halos */}
      <div className="absolute -top-16 -right-12 w-56 h-56 rounded-full opacity-60 pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(139,30,63,0.18) 0%, transparent 70%)" }} />
      <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full opacity-50 pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(216,226,220,0.65) 0%, transparent 70%)" }} />

      {/* Dot grid */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
        <pattern id="pm-dots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1" fill="rgba(10,25,47,0.06)" />
        </pattern>
        <rect width="100%" height="100%" fill="url(#pm-dots)" />
      </svg>

      {/* Header strip */}
      <div className="relative flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#0A192F] text-white">
            <Scale className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">Matter Pipeline</div>
            <div className="text-xs text-slate-600">Live · 6 active matters</div>
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/80 border border-slate-200 text-[10px] font-bold tracking-wider text-[#15803D]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#15803D] drift-slow" /> SYNCED
        </div>
      </div>

      {/* Stack of case-file cards */}
      <div className="relative space-y-2">
        {cases.map((c, i) => (
          <div
            key={c.code}
            className="flex items-center gap-3 rounded-xl bg-white/90 backdrop-blur-sm border border-white p-2.5 shadow-[0_4px_12px_rgba(10,25,47,0.06)] hover:shadow-[0_8px_20px_rgba(139,30,63,0.10)] transition"
            style={{ animation: `rise-in .6s cubic-bezier(0.16,1,0.3,1) ${i * 0.08}s both` }}
          >
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-none ${c.tone}`}>
              <c.Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] tracking-wider text-slate-500">{c.code}</span>
                <span className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#8B1E3F]">{c.status}</span>
              </div>
              <div className="text-[13px] font-semibold text-[#0A192F] truncate">{c.title}</div>
              <div className="mt-1 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${c.pct}%`,
                    background: "linear-gradient(90deg, #0A192F 0%, #8B1E3F 100%)",
                  }}
                />
              </div>
            </div>
            <div className="font-mono text-[11px] font-bold text-[#0A192F] flex-none">{c.pct}%</div>
          </div>
        ))}
      </div>

      {/* Bottom seal */}
      <div className="relative mt-4 flex items-center justify-between text-[10px]">
        <div className="inline-flex items-center gap-2 text-slate-500">
          <span className="font-mono">2026 · Q2</span>
          <span>·</span>
          <span>+47 reports generated</span>
        </div>
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#0A192F] text-white font-bold tracking-wider">
          <ShieldCheck className="h-3 w-3" /> SEALED
        </div>
      </div>
    </div>
  );
}
